import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons/faCalendarCheck";
import { faCircleExclamationCheck } from "@fortawesome/pro-regular-svg-icons/faCircleExclamationCheck";
import { Grid, Typography } from "@mui/material";
import { MprcConfirmDialog, MprcLink, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { MprcProgress } from "components/MprcProgress";
import { TrainingAssessmentItemDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { MprcCardCourseMessageIcon } from "views/lms/student/EnrollmentView/Course/ContentComponents/common/CourseViewComponents";

interface IProps {
    assessment: TrainingAssessmentItemDto;
    enrollmentId: string;
    componentId: string;
    previewMode?: boolean;
    header?: boolean;
    onReload: () => void;
}

/////////////////////////////////////////////////////////////////////////////////////

export const TrainingAssessmentDataBox = (props: IProps) => {

    const { assessment, enrollmentId, componentId, onReload, previewMode, header = false } = props;

    const { t } = useTranslation("lms");
    const api = useCoreApi();

    const [dialog, setDialog] = useState<any>();
    const [discard, setDiscard] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    ///
    const discardTriesByUserHandle = () => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={<span>{t("Student.EnrollmentView.Content.Assessment.ConfirmDiscardTries")}</span>}
                onClose={(result) => {
                    if (result) {
                        setDiscard(true);
                    }
                    setDialog(null);
                }}
            />
        );
    };

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderNoAnswered = () => {
        return (
            <>
                {
                    !previewMode &&
                    <MprcCardCourseMessageIcon
                        icon={faCircleExclamationCheck}
                        title={t("Student.EnrollmentView.Content.Assessment.NoScoreText")}
                        message={
                            assessment?.onlyComponentFinalScore == true && assessment.minFinalScore > 0
                                ? t("Student.EnrollmentView.Content.Assessment.MinFinalScoreText", { count: assessment.minFinalScore })
                                : assessment!.maxTries > 0
                                    ? t("Student.EnrollmentView.Content.Assessment.MaxTriesText", { count: assessment!.maxTries })
                                    : undefined
                        }
                    />
                }
            </>
        );
    };

    ///
    const RenderAnswered = () => {
        return (
            <>
                {!previewMode &&
                    <MprcCardCourseMessageIcon
                        icon={faCircleExclamationCheck}
                        message={
                            <Grid>
                                <Typography variant="subtitle1" component="p" color={assessment!.scorePercent < assessment!.minFinalScore ? "warning" : "success"}>
                                    {t("Student.EnrollmentView.Content.Assessment.ScoreText", { count: assessment!.scorePercent ?? 0 })}
                                </Typography>
                                <MprcProgress
                                    value={assessment!.scorePercent}
                                    color={assessment!.scorePercent < assessment!.minFinalScore ? "warning" : "success"}
                                    width={338}
                                />
                                {assessment?.onlyComponentFinalScore == true && assessment.minFinalScore > 0 &&
                                    <Typography variant="caption" component="p">
                                        {t("Student.EnrollmentView.Content.Assessment.MinFinalScoreText", { count: assessment.minFinalScore })}
                                    </Typography>
                                }
                                {assessment.canAnswer &&
                                    <Typography variant="caption" component="p">
                                        {
                                            t(assessment!.maxTries == 0
                                                ? "Student.EnrollmentView.Content.Assessment.AnswerAgainText"
                                                : "Student.EnrollmentView.Content.Assessment.RemainingTriesText", { count: assessment.remaingTries })
                                        }
                                    </Typography>
                                }
                                {assessment.canDiscard &&
                                    <MprcLink onClick={discardTriesByUserHandle}>
                                        <Typography variant="caption" component="p" color="highlight.main">
                                            {t("Student.EnrollmentView.Content.Assessment.DiscardedByUserText", { count: assessment.remaingTries })}
                                        </Typography>
                                    </MprcLink>
                                }
                            </Grid>
                        }
                    />
                }
            </>
        );
    };

    ///
    const RenderScorePending = () => {
        return (
            <Grid container direction="column" spacing={2}>
                {
                    !header &&
                    <Grid>
                        <MprcCardCourseMessageIcon
                            icon={faCalendarCheck}
                            title={t("Student.EnrollmentView.Content.Assessment.CompletedText")}
                            message={Formaters.performaDate(assessment!.completedOn, { dayOfWeek: true, preposition: "none" })}
                        />
                    </Grid>
                }
                <Grid>
                    <MprcCardCourseMessageIcon
                        icon={faCircleExclamationCheck}
                        title={t("Student.EnrollmentView.Content.Assessment.ScorePendingText")}
                        message={t("Student.EnrollmentView.Content.Assessment.CorrectionPendingText")}
                    />
                </Grid>
            </Grid>
        );
    };

    /////////////////////////////////////////////////////////////////////////////////////
    /// EFFECT
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.enrollment.discardMaxTriesAssEnrollment({
                    enrollmentId,
                    componentId
                });
                if (result) onReload();
            } catch (e) {
                setViewError(e);
            } finally {
                setDiscard(false);
            }
        }

        if (discard) postData();

    }, [discard]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <MprcRequestErrorReport error={viewError} />
            <MprcRequestIndicator type={"post"} visible={discard} />

            {
                assessment.scorePending
                    ? RenderScorePending()
                    : assessment.tries == 0
                        ? RenderNoAnswered()
                        : RenderAnswered()
            }

        </>
    );
};
